import { apiDelete, apiPost, type Envelope } from "./client.js";
import { useApi, type ApiState } from "./useApi.js";
import type { Role } from "../roles.js";

export interface DeclaredEventView {
  readonly source: string;
  readonly method: string;
  readonly confidence: number;
  readonly effectiveAt: string;
  readonly recordedAt: string;
  readonly declaredBy: string | null;
}

export interface AdminPortfolioProject {
  readonly id: string;
  readonly name: string;
  readonly scopeType: string;
  readonly parentProjectId: string | null;
  readonly status: string;
  readonly profile: {
    readonly workflowProfile: string | null;
    readonly stageModel: string | null;
    readonly confidenceBasis: string | null;
    readonly deliveryMode: string | null;
    readonly targetDate: string | null;
  };
  readonly projectDeclared: DeclaredEventView;
  readonly profileDeclared: DeclaredEventView | null;
}

export interface SourceMapping {
  readonly id: string;
  readonly source: string;
  readonly key: string;
  readonly createdAt: string;
}

export interface CreateProgramInput {
  readonly name: string;
  readonly status: string;
}

export interface CreateProjectInput {
  readonly name: string;
  readonly status: string;
  readonly parentProjectId: string | null;
}

export interface UpdateProjectInput {
  readonly name?: string;
  readonly status?: string;
}

export interface DeclareProfileInput {
  readonly workflowProfile?: string | null;
  readonly stageModel?: string | null;
  readonly confidenceBasis?: string | null;
  readonly deliveryMode?: string | null;
}

export interface AdminPortfolioService {
  usePortfolio(revision: number): ApiState<AdminPortfolioProject[]>;
  useMappings(projectId: string, revision: number): ApiState<SourceMapping[]>;
  addMapping(
    token: string,
    projectId: string,
    input: { source: string; key: string },
  ): Promise<SourceMapping>;
  removeMapping(token: string, projectId: string, mappingId: string): Promise<void>;
  createProgram(token: string, input: CreateProgramInput): Promise<AdminPortfolioProject>;
  createProject(token: string, input: CreateProjectInput): Promise<AdminPortfolioProject>;
  updateProject(
    token: string,
    projectId: string,
    input: UpdateProjectInput,
  ): Promise<AdminPortfolioProject>;
  declareProfile(
    token: string,
    projectId: string,
    input: DeclareProfileInput,
  ): Promise<AdminPortfolioProject>;
  setTargetDate(token: string, projectId: string, targetDate: string | null): Promise<void>;
  reparentProject(
    token: string,
    projectId: string,
    parentProjectId: string | null,
  ): Promise<AdminPortfolioProject>;
}

export const ADMIN_PORTFOLIO_ROLES: readonly Role[] = ["admin"];

export function canManagePortfolio(role: Role): boolean {
  return ADMIN_PORTFOLIO_ROLES.includes(role);
}

async function unwrap<T>(envelope: Promise<Envelope<T>>): Promise<T> {
  return (await envelope).data;
}

export const liveAdminPortfolioService: AdminPortfolioService = {
  usePortfolio: (revision) => useApi<AdminPortfolioProject[]>(`/admin/portfolio?rev=${revision}`),
  useMappings: (projectId, revision) =>
    useApi<SourceMapping[]>(`/admin/projects/${projectId}/source-mappings?rev=${revision}`),
  addMapping: (token, projectId, input) =>
    unwrap(
      apiPost<SourceMapping>(`/admin/projects/${projectId}/source-mappings`, {
        token,
        body: input,
      }),
    ),
  removeMapping: (token, projectId, mappingId) =>
    apiDelete(`/admin/projects/${projectId}/source-mappings/${mappingId}`, { token }),
  createProgram: (token, input) =>
    unwrap(apiPost<AdminPortfolioProject>("/admin/programs", { token, body: input })),
  createProject: (token, input) =>
    unwrap(apiPost<AdminPortfolioProject>("/admin/projects", { token, body: input })),
  updateProject: (token, projectId, input) =>
    unwrap(apiPost<AdminPortfolioProject>(`/admin/projects/${projectId}`, { token, body: input })),
  declareProfile: (token, projectId, input) =>
    unwrap(
      apiPost<AdminPortfolioProject>(`/admin/projects/${projectId}/profile`, {
        token,
        body: input,
      }),
    ),
  setTargetDate: (token, projectId, targetDate) =>
    unwrap(apiPost(`/projects/${projectId}/target-date`, { token, body: { targetDate } })).then(
      () => undefined,
    ),
  reparentProject: (token, projectId, parentProjectId) =>
    unwrap(
      apiPost<AdminPortfolioProject>(`/admin/projects/${projectId}/reparent`, {
        token,
        body: { parentProjectId },
      }),
    ),
};
